import {useState} from 'react'
import {useDispatch} from 'react-redux'
import * as Yup from 'yup'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import * as auth from '../redux/AuthRedux'
import {login} from '../redux/AuthCRUD'
import {useAlert} from '../../../hooks/utils/useAlert'
import apiClient from '../../../helpers/apiClient'

const loginSchema = Yup.object().shape({
  email: Yup.string().email('Wrong email format').required('This field is required'),
  password: Yup.string().required('This field is required'),
})

const initialValues = {email: '', password: ''}

export function Login() {
  const [loading, setLoading] = useState(false)
  const [showPwd, setShowPwd] = useState(false)
  const dispatch = useDispatch()
  const {showError} = useAlert()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, formikHelpers) => {
      const {setSubmitting, resetForm} = formikHelpers

      try {
        setLoading(true)
        const data = await login(values.email, values.password)
        dispatch(auth.actions.login(data.access,data.refresh))
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${data.access}`
        resetForm({ values: { email: values.email, password: '' } })
      } catch (error) {
        const statusCode = error?.response?.status || 500

        showError({
          title: 'Unable to Sign In',
          message: (error as any)?.response?.data?.detail ?? "We couldn't sign you in. Please check your credentials and try again.",
          errorCode: `AUTH_${statusCode}`,
        })
      } finally {
        setLoading(false)
        setSubmitting(false)
      }
    }
  })

  return (
    <>
      <form
        className='form nb-form-520'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        {/* Header  */}
        <div className='nb-auth-header'>
          <h2 className='nb-heading-h2'>Welcome to Nobilis</h2>
          <div className='nb-subtitle'>Enter your credentials to login.</div>
        </div>

        <div className='nb-fields-section'>
          {/* Campo Email */}
          <div className='nb-field'>
            <label className='nb-tag'>EMAIL</label>
            <div className='nb-field-stack'>
              <input
                {...formik.getFieldProps('email')}
                type='email'
                autoComplete='off'
                className='form-control form-control-underline input-text-style'
              />
            </div>
            {formik.touched.email && formik.errors.email && (
              <div className='fv-plugins-message-container'>
                <span role='alert' className='fv-help-block input-text-style fs-8'>
                  {formik.errors.email}
                </span>
              </div>
            )}
          </div>

          <div className='nb-password-section'>
            <div className='nb-field'>
              <label className='nb-tag'>PASSWORD</label>
              <div className='nb-field-stack'>
                <div className='nb-input-wrap'>
                  <input
                    type={showPwd ? 'text' : 'password'}
                    autoComplete='off'
                    {...formik.getFieldProps('password')}
                    className='form-control form-control-underline'
                  />
                  <button
                    type='button'
                    className='nb-input-eye'
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPwd((s) => !s)}
                  >
                    <div>
                      <i className={`bi ${showPwd ? 'bi-eye-slash' : 'bi-eye'}`} />
                    </div>
                  </button>
                </div>
              </div>
              {formik.touched.password && formik.errors.password && (
                <div className='fv-plugins-message-container'>
                  <span role='alert' className='fv-help-block input-text-style fs-8'>
                    {formik.errors.password}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Forgot password*/}
          <div className='nb-auth-links'>
            <Link to='/auth/forgot-password' className='nb-link-underline'>
              FORGOT PASSWORD?
            </Link>
          </div>
        </div>

        {/* Sección de botones */}
        <div className='nb-auth-actions'>
          <button
            type='submit'
            className='btn nb-btn-primary'
            disabled={loading}
            aria-busy={loading ? 'true' : 'false'}
            aria-live='polite'
          >
            {!loading ? (
              <>
                <span className='nb-heading-md'>LOG IN</span>
                <img
                  src='/media/svg/nobilis/vector1.svg'
                  alt=''
                  className='nb-btn-icon nb-btn-icon--white'
                />
              </>
            ) : (
              <span className='indicator-progress nb-heading-md'>
                Please wait...
                <span
                  className='spinner-border spinner-border-sm align-middle ms-2'
                  role='status'
                  aria-hidden='true'
                />
              </span>
            )}
          </button>

          <Link to='/auth/registration' className='btn nb-btn-outline'>
            <span className='nb-heading-md'>REQUEST MEMBERSHIP</span>
            <img
              src='/media/svg/nobilis/vector1.svg'
              alt=''
              className='nb-btn-icon nb-btn-icon--black'
            />
          </Link>
        </div>
      </form>
    </>
  )
}
